<template>
  <v-container>
    <v-row>
      <v-col
        cols=12
        sm=6
        md=3
      >
        <v-select
          :items="vesselTypes"
          :label="$t('type')"
          :item-text="item => $t(item.text)"
          item-value="value"
          v-model="vessel.type"
          clearable
        ></v-select>
      </v-col>

      <v-col
        cols=12
        sm=6
        md=3
      >
        <v-text-field
          :label="$t('name')"
          v-model="vessel.name"
        />
      </v-col>

      <v-col
        cols=12
        sm=5
        md=2
      >
        <v-text-field
          :label="$t('registration')"
          v-model="vessel.registration"
        />
      </v-col>

      <v-col
        cols=6
        sm=4
        md=2
      >
        <v-text-field
          :label="$t('imo')"
          v-model="vessel.imo"
        />
      </v-col>

      <v-col
        cols=6
        sm=3
        md=2
      >
        <v-text-field
          :label="$t('mmsi')"
          v-model="vessel.mmsi"
        />
      </v-col>

      <v-col
        cols=6
        sm=4
        md=3
      >
        <v-text-field
          :label="$t('call_sign')"
          v-model="vessel.call_sign"
        />
      </v-col>

      <v-col
        cols=6
        sm=4
        >
        <v-text-field
          :label="$t('brand')"
          v-model="vessel.brand"
        />
      </v-col>

      <v-col
        cols=6
        sm=4
      >
        <v-text-field
          :label="$t('model')"
          v-model="vessel.model"
        />
      </v-col>

      <v-col
        cols=6
        sm=3
        md=3
      >
        <year-picker
          :label="$t('year_of_build')"
          :picker.sync="vessel.yearPicker"
        ></year-picker>
      </v-col>
      <v-col
        cols=6
        sm=2
      >
        <v-text-field
          :label="$t('length')"
          v-model="vessel.length"
          type="number"
        />
      </v-col>
      <v-col
        cols=6
        sm=2
      >
        <v-text-field
          :label="$t('tonnage')"
          v-model="vessel.tonnage"
          type="number"
        />
      </v-col>
      <v-col
        cols=6
        sm=2
      >
        <v-text-field
          :label="$t('hull_colour')"
          v-model="vessel.hull_colour"
        />
      </v-col>

      <v-col
        cols=6 sm=3>
        <v-text-field
          :label="$t('home_port')"
          v-model="vessel.home_port"
        />
      </v-col>

      <v-col
        cols=12
        sm=4
        md=3
      >
        <v-select
          :items="countries"
          :label="$t('flag')"
          :item-text="item => $t(item.text)"
          item-value="value"
          v-model="vessel.flag"
          clearable
        ></v-select>
      </v-col>

      <v-col
        cols=12
        sm=8
        md=6
      >
        <v-text-field
          :label="$t('distinguishing_features')"
          v-model="vessel.distinguishing_features"
        />
      </v-col>

      <v-col
        cols=12
        sm=6
      >
        <v-text-field
          hide-details
          :label="$t('last_position')"
          v-model="vessel.last_position"
          @focus="show_position_warning = true"
          @blur="show_position_warning = false"
        />
        <v-alert
          class="mt-1"
            v-show="show_position_warning"
            dense
            text
            type="warning"
          > {{ $t('last_position_warning') }}
          </v-alert>
      </v-col>

      <v-col
        cols=12
        sm=6
      >
        <vessel-control ref="vesselMonitoring"></vessel-control>
      </v-col>

      <v-col
        cols=12
        sm=6>
        <h2
          class="mb-3"
        >{{ $t('action_required') }}</h2>
        <v-row>
          <v-col>
            <v-checkbox
              v-if="requestCategory === 'category_1'"
              v-model="vessel.no_action"
              @click="clearActions(vessel)"
              :label="$t('no_action')"
              dense
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row
        class="mt-2">
          <v-col cols=6>
            <v-checkbox
              :disabled="vessel.no_action !== '' && vessel.no_action"
              v-model="vessel.sighting_report"
              :label="$t('sighting_report')"
              dense
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols=6>
            <v-checkbox
              :disabled="vessel.no_action !== '' && vessel.no_action"
              v-model="vessel.vessel_boarding"
              :label="$t('vessel_boarding')"
              dense
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols=6>
            <v-checkbox
              :disabled="vessel.no_action !== '' && vessel.no_action"
              v-model="vessel.port_control"
              :label="$t('port_control')"
              dense
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols=6>
            <v-checkbox
              :disabled="vessel.no_action !== '' && vessel.no_action"
              :label="$t('vessel_inspection')"
              v-model="vessel.vessel_inspection"
              dense
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col cols=6>
            <v-checkbox
              :disabled="vessel.no_action !== '' && vessel.no_action"
              :label="$t('vessel_other_actions')"
              v-model="vessel.other_actions"
              dense
              hide-details
            ></v-checkbox>
          </v-col>

          <v-col
            cols=12
            v-if="vessel.other_actions"
          >
            <v-textarea
              visible
              outlined
              name="input-7-4"
              :label="$t('indicate_other_actions')"
              v-model="vessel.other_actions_info"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { maocData } from '../../services/MaocData'
import YearPicker from '../ui/YearPicker'
import { ExportUtils } from '../../services/DataExport'
import { VesselUtils } from '../../services/VesselUtils'
import VesselControl from './VesselControl'

export default {
  name: 'VesselDetails',
  props: ['requestCategory'],
  components: {
    VesselControl,
    YearPicker
  },
  data: vm => ({
    vesselTypes: maocData.dynamicLocalizedSelect('vessels'),
    countries: maocData.dynamicLocalizedSelect('countries'),
    menu: false,
    pickedDate: null,
    vessel: {
      no_action: '',
      sighting_report: '',
      vessel_boarding: '',
      port_control: '',
      vessel_inspection: '',
      other_actions: '',
      year_of_build: '',
      year: '',
      menu: '',
      yearPicker: YearPicker.newPickerObject()
    },
    show_position_warning: false
  }),
  methods: {
    clearActions (vessel) {
      vessel.sighting_report = false
      vessel.vessel_boarding = false
      vessel.port_control = false
      vessel.vessel_inspection = false
      vessel.other_actions = false
    },
    getData: function () {
      this.vessel.year_of_build = this.vessel.yearPicker.year

      const monitoringData = this.$refs.vesselMonitoring.getData()
      if (Object.keys(monitoringData).length > 0) {
        this.vessel.monitoring = monitoringData
      }

      if (this.vessel.type) {
        this.vessel.type_en = this.$t('vessels.' + this.vessel.type, 'en')
      }
      if (this.vessel.flag) {
        this.vessel.flag_en = this.$t('countries.' + this.vessel.flag, 'en')
      }
      if (VesselUtils.vesselActionRequired(this.vessel)) {
        this.vessel.required_action_en = ExportUtils.formatVesselRequiredActions(this.vessel)
      }
      return ExportUtils.copyObject(this.vessel, ['yearPicker'])
    },
    setData: function (data) {
      this.$refs.vesselMonitoring.setData(data.monitoring)
      delete data.monitoring
      this.vessel = Object.assign(this.vessel, data)
      this.vessel.yearPicker.year = this.vessel.year_of_build
      this.$forceUpdate()
    }
  }
}
</script>
